// 极简模态：portal 到 body；ESC 关闭；点遮罩关闭。
// 不引入 radix；focus-trap 暂用 inert 文档辅助技术：仅在打开时阻止背景滚动。

import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className,
}: DialogProps) {
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === "string" ? title : undefined}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div
        className={cn(
          "max-h-[90vh] w-[min(640px,92vw)] overflow-y-auto rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 shadow-lg",
          className,
        )}
      >
        {title ? (
          <h2 className="mb-1 text-base font-semibold">{title}</h2>
        ) : null}
        {description ? (
          <p className="mb-3 text-xs text-[hsl(var(--muted-foreground))]">
            {description}
          </p>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  );
}
